import { Component, HostListener, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { CompanyFormComponent } from '../company-form/company-form.component';
import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';
import { clearCompanyCache, setActiveCompany } from '../../../store/actions/company-form.actions';
import { UserSessionTrackerService } from '../../../services/user-session-tracker.service';
import { selectCompanyCode, selectCompanyName, selectCompanyCountry } from '../../../store/selectors/company-form.selectors';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CurrentUserState } from '../../../state/current-user.state';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';
import { AppUserCompanyAccessType } from '../../../models/enums/appUserCompanyAccessType.enum';
import { Category } from '../../../models/enums/category.enum';
import { FixDirectorsDialogComponent } from '../fix-directors-dialog/fix-directors-dialog.component';

@Component({
  selector: 'app-company-edit',
  imports: [
    CompanyFormComponent,
    NgxSpinnerModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './company-edit.component.html',
  styleUrl: './company-edit.component.scss'
})
export class CompanyEditComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  @ViewChild(CompanyFormComponent) companyForm?: CompanyFormComponent;
  companyId: string | null = null;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private sessionTracker = inject(UserSessionTrackerService);
  private currentUserState = inject(CurrentUserState);
  private dialog = inject(MatDialog);

  isUserRole = this.currentUserState.user()?.role === AppUserRole.User;
  canFixDirectors = (() => {
    const user = this.currentUserState.user();
    const role = user?.role;
    if (role === AppUserRole.Admin) return true;
    // Fix Directors rewrites board membership periods, so it needs the Board tab for real, not read-only.
    if (this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Board)) return false;
    return (role === AppUserRole.User || role === AppUserRole.Leader)
      && (user?.category === Category.CorporateGovernance || user?.category === Category.VotingResults);
  })();
  isDeveloper = this.currentUserState.isDeveloper();
  companyCode = this.store.selectSignal(selectCompanyCode);
  companyName = this.store.selectSignal(selectCompanyName);
  companyCountry = this.store.selectSignal(selectCompanyCountry);
  processStatus = signal<{ year: string; hasData: boolean; categories: { key: string; label: string; shortLabel: string; isChecked: boolean; isFinished: boolean; checkedDate: string | null; checkedByUser: string | null; finishedDate: string | null; finishedByUser: string | null }[] } | null>(null);

  hasUnsavedChanges(): boolean {
    return this.companyForm?.hasUnsavedChanges() ?? false;
  }

  // Warn on browser tab close / refresh / external navigation. Browsers only allow
  // a native, non-customizable prompt here — the Material confirmation dialog cannot
  // be shown during unload. The in-app canDeactivate guard still handles SPA navigation.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      // Legacy browsers require returnValue to be set to trigger the prompt.
      event.returnValue = true;
    }
  }

  ngOnInit() {
    this.companyId = this.route.snapshot.paramMap.get('id');
    if (this.companyId) {
      this.store.dispatch(setActiveCompany({ companyId: this.companyId }));
    }
  }

  ngOnDestroy() {
    this.sessionTracker.endCurrentSession();
    this.store.dispatch(clearCompanyCache());
  }

  onSubmit() {
    // TODO: implement update logic
  }

  onCancel() {
    this.router.navigate(['/companies']);
  }

  /** Opens the audit log for this company (everything related to it) in a new tab. */
  onViewAuditLog() {
    if (!this.companyId) return;
    window.open(`/auditlog?companyId=${encodeURIComponent(this.companyId)}`, '_blank', 'noopener');
  }

  onFixDirectors() {
    if (!this.companyId) return;
    this.dialog.open(FixDirectorsDialogComponent, {
      data: { companyId: this.companyId },
      maxHeight: '98vh',
      maxWidth: '90vw',
      panelClass: 'fix-directors-dialog-panel',
      autoFocus: false,
      restoreFocus: false
    });
  }

  formatTooltip(date: string | null, user: string | null): string {
    const parts: string[] = [];
    if (user) parts.push(user);
    if (date) {
      const d = new Date(date);
      parts.push(d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }));
    }
    return parts.join(' — ');
  }
}
